(() => {
  const instances = new WeakMap();
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  class LoveEcruProduct {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-le-config]').textContent);
      this.variants = new Map(this.config.variants.map(variant => [String(variant.id), variant]));
      this.media = new Map(this.config.media.map(item => [String(item.id), item]));
      this.form = root.querySelector('form[data-product-form]');
      this.variantInput = this.form.querySelector('[name="id"]');
      this.quantityInput = root.querySelector('[data-le-quantity]');
      this.track = root.querySelector('[data-le-gallery-track]');
      this.thumbs = root.querySelector('[data-le-gallery-thumbs]');
      this.sticky = root.querySelector('[data-le-sticky-cart]');
      this.mainButton = root.querySelector('[data-le-main-atc]');
      this.dialog = root.querySelector('[data-le-zoom-dialog]');
      this.controller = new AbortController();
      this.requestPending = false;
      this.refreshPromise = null;
      this.galleryIndex = 0;
      this.quantities = { single: 1, set: 1 };
      this.lastSingle = null;
      this.current = null;
      this.frame = null;
      this.status = root.querySelector('[data-le-status]');
      if (!this.status) {
        this.status = document.createElement('span');
        this.status.className = 'visually-hidden';
        this.status.dataset.leStatus = '';
        this.status.setAttribute('role', 'status');
        this.status.setAttribute('aria-live', 'polite');
        this.root.append(this.status);
      }
      this.listen();
      const fromUrl = this.variants.get(new URL(window.location.href).searchParams.get('variant'));
      const initial = fromUrl || this.variants.get(String(this.config.initialId)) || this.defaultVariant();
      this.selectVariant(initial, { updateUrl: false, announce: false, initialGallery: true });
      this.installNativeBridge();
      this.root.dataset.leEnhanced = 'true';
      const fallbackSelector = this.root.querySelector('[data-le-native-select]');
      if (fallbackSelector) fallbackSelector.hidden = true;
      this.observer = new MutationObserver(() => this.scheduleSticky());
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'aria-hidden'] });
      this.scheduleSticky();
    }

    get state() { return this.root.leFormState; }
    get busy() { return this.requestPending || this.state?.isLoading || this.state?.isSuccess; }
    get quantity() { return this.normalizeQuantity(this.quantityInput?.value || 1); }

    defaultVariant() {
      return this.config.variants.find(variant => !variant.isSet && variant.available) || this.config.variants.find(variant => variant.available) || this.config.variants[0];
    }

    money(value) {
      if (this.state?.$formatCurrency) {
        return this.state.$formatCurrency(value, { currency: this.config.currency, locale: this.config.locale, form: this.config.showCurrencyCode !== false ? 'explicit' : 'short' });
      }
      const formatted = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || undefined, { style: 'currency', currency: this.config.currency }).format(value / 100);
      return this.config.showCurrencyCode !== false ? `${formatted} ${this.config.currency}` : formatted;
    }

    listenOn(target, event, listener, options = {}) {
      target?.addEventListener(event, listener, { ...options, signal: this.controller.signal });
    }

    listen() {
      this.listenOn(this.root, 'click', event => this.onClick(event));
      this.listenOn(this.form, 'submit', event => {
        this.installNativeBridge();
        if (!this.state) return;
        if (this.busy || !this.current.available) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!this.current.available) this.status.textContent = this.config.unavailable;
          return;
        }
        this.setQuantity(this.quantity);
        this.variantInput.value = String(this.current.id);
        this.state.variantId = this.current.id;
        this.state.quantity = this.quantity;
        this.requestPending = true;
        this.status.textContent = this.config.adding;
        this.toggle('[data-le-error]', false);
      }, { capture: true });
      this.listenOn(this.variantInput, 'change', () => {
        const variant = this.variants.get(this.variantInput.value);
        if (variant) this.selectVariant(variant);
      });
      this.listenOn(this.quantityInput, 'input', () => this.renderTotals());
      this.listenOn(this.quantityInput, 'change', () => this.setQuantity(this.quantity));
      this.listenOn(this.root, 'le:cart-state', () => {
        this.installNativeBridge();
        if (!this.state?.isLoading) this.requestPending = false;
        this.renderCartState();
      });
      this.listenOn(this.root, 'keydown', event => {
        const button = event.target.closest('[data-le-variant][role="button"]');
        if (button && event.key === ' ') {
          event.preventDefault();
          button.click();
        }
      });
      this.listenOn(document, 'alpine:initialized', () => {
        this.installNativeBridge();
        this.renderCartState();
      });
      this.listenOn(window, 'popstate', () => {
        if (this.busy) return;
        const requested = this.variants.get(new URL(window.location.href).searchParams.get('variant'));
        this.selectVariant(requested || this.defaultVariant(), { updateUrl: false });
      });
      this.listenOn(window, 'resize', () => {
        this.renderGalleryCounter();
        this.scheduleSticky();
      }, { passive: true });
      this.listenOn(window, 'scroll', () => this.scheduleSticky(), { passive: true });
      this.listenOn(this.track, 'scroll', () => this.renderGalleryCounter(), { passive: true });
      this.listenOn(this.track, 'keydown', event => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.goToImage(this.galleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
      this.listenOn(this.dialog, 'click', event => {
        if (event.target === this.dialog || event.target.closest('[data-le-zoom-close]')) this.closeZoom();
      });
      this.listenOn(this.dialog, 'close', () => {
        this.zoomReturn?.focus({ preventScroll: true });
        this.scheduleSticky();
      });
      this.listenOn(this.dialog, 'cancel', () => this.scheduleSticky());
      this.listenOn(document, 'click', event => {
        const setLink = event.target.closest('[data-le-select-set]');
        const returnLink = event.target.closest('[data-le-return-to-purchase], [data-le-sticky-cart] a[href="#le-product-purchase"]');
        if (!this.root.isConnected || (!setLink && !returnLink) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (returnLink) {
          event.preventDefault();
          this.returnToPurchase();
          return;
        }
        const set = this.config.variants.find(variant => variant.isSet);
        if (!set) return;
        event.preventDefault();
        if (!this.busy) this.selectVariant(set);
        this.returnToPurchase('set');
      });
      this.listenOn(document, 'theme:drawer:open', () => this.scheduleSticky(), { capture: true });
      this.listenOn(document, 'theme:drawer:close', () => this.scheduleSticky(), { capture: true });
    }

    installNativeBridge() {
      const state = this.state;
      if (!state || this.nativeInstalled) return;
      this.nativeInstalled = true;
      state.variantId = this.current.id;
      state.quantity = this.quantity;
      state.leAvailable = Boolean(this.current.available);
      const add = state.addToCart.bind(state);
      const update = state.updateHeaderTotal.bind(state);
      const popdown = state.openPopdown.bind(state);
      const reload = state.reloadCart.bind(state);
      const handleError = state.handleError.bind(state);
      const nativeErrorDisplay = this.root.querySelector('[data-error-display]');
      if (nativeErrorDisplay && this.root.querySelector('[data-le-error]')) nativeErrorDisplay.hidden = true;
      state.addToCart = payload => add(payload).catch(error => {
        if (error?.data) throw error;
        if (error?.response?.data) throw { data: error.response.data };
        throw { data: { description: this.config.networkError } };
      });
      state.updateHeaderTotal = () => {
        this.refreshPromise = Promise.resolve(update());
        return this.refreshPromise;
      };
      state.openPopdown = variant => {
        const drawer = document.querySelector('[data-drawer="drawer-cart"]');
        if (!drawer) return popdown(variant);
        Promise.resolve(this.refreshPromise).then(() => {
          drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
          this.scheduleSticky();
        });
      };
      state.reloadCart = () => Promise.resolve(this.refreshPromise).then(() => reload());
      state.handleError = error => {
        this.requestPending = false;
        handleError(error);
        this.status.textContent = state.error || this.config.networkError;
        this.text('[data-le-error]', (state.error || this.config.networkError).replace(/<br\s*\/?>/gi, '\n'));
        this.toggle('[data-le-error]', true);
        this.renderCartState();
      };
      this.renderCartState();
    }

    normalizeQuantity(value) {
      const minimum = Math.max(1, Number(this.current?.min) || 1);
      const step = Math.max(1, Number(this.current?.step) || 1);
      const maximum = Number(this.current?.max) > 0 ? Number(this.current.max) : Number.MAX_SAFE_INTEGER;
      const entered = Number(value);
      const whole = Number.isFinite(entered) ? Math.floor(entered) : minimum;
      const adjusted = minimum + Math.floor((Math.max(minimum, whole) - minimum) / step) * step;
      return Math.min(minimum + Math.floor((maximum - minimum) / step) * step, adjusted);
    }

    setQuantity(value, notify = false) {
      const quantity = this.normalizeQuantity(value);
      if (this.quantityInput) this.quantityInput.value = String(quantity);
      this.quantities[this.current.isSet ? 'set' : 'single'] = quantity;
      if (this.state) this.state.quantity = quantity;
      this.renderTotals();
      if (notify) this.quantityInput?.dispatchEvent(new Event('change', { bubbles: true }));
    }

    variantUrl(variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      url.searchParams.delete('option_values');
      return url;
    }

    selectVariant(variant, { updateUrl = true, announce = true, initialGallery = false } = {}) {
      if (!variant || this.busy) return;
      const previous = this.current;
      const kind = variant.isSet ? 'set' : 'single';
      if (previous) this.quantities[previous.isSet ? 'set' : 'single'] = this.quantity;
      this.current = variant;
      if (!variant.isSet) this.lastSingle = variant;
      this.variantInput.value = String(variant.id);
      if (this.state) {
        this.state.variantId = variant.id;
        this.state.leAvailable = Boolean(variant.available);
      }
      if (this.quantityInput) {
        this.quantityInput.min = String(Math.max(1, Number(variant.min) || 1));
        this.quantityInput.step = String(Math.max(1, Number(variant.step) || 1));
        if (Number(variant.max) > 0) this.quantityInput.max = String(variant.max);
        else this.quantityInput.removeAttribute('max');
      }
      this.setQuantity(previous && previous.isSet === variant.isSet ? this.quantities[kind] : this.quantities[kind] || 1);
      this.root.dataset.leSelectedOffer = kind;
      this.root.dataset.leSelectedVariant = String(variant.id);
      this.root.dataset.leAvailable = String(Boolean(variant.available));
      this.root.querySelectorAll('[data-le-variant]').forEach(control => {
        const selected = control.dataset.leVariant === String(variant.id);
        const choice = this.variants.get(control.dataset.leVariant);
        control.classList.toggle('is-selected', selected);
        if (control.tagName === 'A' && choice) control.href = this.variantUrl(choice).toString();
        if (selected) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
        if (control.getAttribute('role') === 'radio') control.setAttribute('aria-checked', String(selected));
        if (control.tagName === 'BUTTON' || control.getAttribute('role') === 'button') control.setAttribute('aria-pressed', String(selected));
      });
      this.root.querySelectorAll('[data-le-offer]').forEach(control => {
        const selected = control.dataset.leOffer === kind;
        control.classList.toggle('is-selected', selected);
        control.setAttribute('aria-pressed', String(selected));
      });
      this.toggle('[data-le-colors]', !variant.isSet);
      this.toggle('[data-le-set-included]', variant.isSet);
      this.toggle('[data-le-quantity-presets]', !variant.isSet);
      this.toggle('[data-le-quantity-note]', variant.isSet);
      this.root.querySelectorAll('[data-le-quantity-preset]').forEach(control => { control.hidden = variant.isSet; });
      this.text('[data-le-finish]', variant.finish || '');
      this.text('[data-le-price]', variant.priceFormatted || this.money(variant.price));
      this.text('[data-le-compare]', variant.compare > variant.price ? variant.compareFormatted || this.money(variant.compare) : '');
      this.toggle('[data-le-compare]', variant.compare > variant.price);
      const saving = variant.isSet ? Number(this.config.setSavings) || 0 : 0;
      this.text('[data-le-savings]', saving > 0 ? (this.config.savingTemplate || 'Save {amount} compared with single items.').replace(/\{\{\s*amount\s*\}\}|\{amount\}|\[amount\]/g, this.money(saving)) : '');
      this.toggle('[data-le-savings]', saving > 0);
      this.text('[data-le-included]', variant.included || '');
      this.text('[data-le-current-label]', variant.label || variant.title);
      this.text('[data-le-quantity-label]', variant.isSet ? this.config.setsLabel || 'Number of sets' : this.config.quantityLabel || 'Quantity');
      document.querySelectorAll('[data-le-detail-finish]').forEach(element => { element.textContent = variant.finish || ''; element.hidden = !variant.finish; });
      document.querySelectorAll('[data-le-detail-included]').forEach(element => { element.textContent = variant.included || ''; element.hidden = !variant.included; });
      this.renderGallery(initialGallery && this.config.initialGalleryIds?.length ? { ...variant, mediaIds: this.config.initialGalleryIds } : variant);
      this.renderCartState();
      if (updateUrl) window.history.replaceState(window.history.state, '', this.variantUrl(variant));
      if (announce) this.status.textContent = `${variant.label || variant.title}. ${variant.priceFormatted || this.money(variant.price)}. ${variant.available ? variant.finish || '' : this.config.soldOut}`;
    }

    text(selector, value) {
      this.root.querySelectorAll(selector).forEach(element => { element.textContent = value; });
    }

    toggle(selector, visible) {
      this.root.querySelectorAll(selector).forEach(element => { element.hidden = !visible; });
    }

    renderTotals() {
      if (!this.current) return;
      const quantity = this.quantity;
      const units = quantity * Math.max(1, Number(this.current.units) || 1);
      if (this.state) this.state.quantity = quantity;
      const total = this.money(this.current.price * quantity);
      this.text('[data-le-total]', total);
      const loading = this.state?.isLoading || this.requestPending;
      const success = this.state?.isSuccess;
      const label = loading ? this.config.adding : success ? this.config.added : this.current.available ? this.config.add : this.config.soldOut;
      const accessibleLabel = `${label}${this.current.available && !loading && !success ? ` — ${total}` : ''}`;
      this.root.querySelectorAll('[data-add-to-cart], [data-le-sticky-cart] button[type="submit"]').forEach(button => {
        button.setAttribute('aria-label', accessibleLabel);
      });
      const unit = units === 1 ? this.config.unitSingular || 'mug' : this.config.unitPlural || 'mugs';
      const set = quantity === 1 ? this.config.setSingular || 'set' : this.config.setPlural || 'sets';
      this.text('[data-le-quantity-note]', this.current.isSet ? `${quantity} ${set} · ${units} ${unit} ${this.config.totalLabel || 'total'}` : '');
      this.text('[data-le-sticky-description]', `${this.current.label || this.current.title}${quantity > 1 ? ` · ${quantity} ${this.current.isSet ? set : unit}` : ''}`);
      this.root.querySelectorAll('[data-le-quantity-preset]').forEach(control => {
        control.setAttribute('aria-pressed', String(quantity === Number(control.dataset.leQuantityPreset)));
      });
      this.root.querySelectorAll('[data-le-quantity-step]').forEach(control => {
        const next = quantity + Number(control.dataset.leQuantityStep) * Math.max(1, Number(this.current.step) || 1);
        control.disabled = this.busy || this.normalizeQuantity(next) === quantity;
      });
    }

    renderCartState() {
      const loading = this.state?.isLoading || this.requestPending;
      const success = this.state?.isSuccess;
      const label = loading ? this.config.adding : success ? this.config.added : this.current.available ? this.config.add : this.config.soldOut;
      this.text('[data-le-add-label]', label);
      this.root.querySelectorAll('[data-add-to-cart], [data-le-sticky-cart] button[type="submit"]').forEach(button => {
        button.disabled = Boolean(loading || success || !this.current.available);
        button.setAttribute('aria-busy', String(Boolean(loading)));
      });
      this.root.querySelectorAll('[data-le-offer], [data-le-quantity-preset]').forEach(control => { control.disabled = Boolean(loading || success); });
      this.root.querySelectorAll('[data-le-variant]').forEach(control => { control.setAttribute('aria-disabled', String(Boolean(loading || success))); });
      if (this.quantityInput) this.quantityInput.readOnly = Boolean(loading || success);
      this.form.setAttribute('aria-busy', String(Boolean(loading)));
      if (success) this.status.textContent = this.config.added;
      this.renderTotals();
      this.scheduleSticky();
    }

    onClick(event) {
      const control = event.target.closest('[data-le-variant], [data-le-offer], [data-le-quantity-step], [data-le-quantity-preset], [data-le-gallery-step], [data-le-media], [data-le-zoom]');
      if (!control || !this.root.contains(control)) return;
      if (control.hasAttribute('data-le-variant')) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        const variant = this.variants.get(control.dataset.leVariant);
        if (variant) this.selectVariant(variant);
      } else if (control.hasAttribute('data-le-offer')) {
        event.preventDefault();
        const variant = control.dataset.leOffer === 'set' ? this.config.variants.find(item => item.isSet) : this.lastSingle || this.defaultVariant();
        this.selectVariant(variant);
      } else if (control.hasAttribute('data-le-quantity-step')) {
        if (!this.busy) this.setQuantity(this.quantity + Number(control.dataset.leQuantityStep) * Math.max(1, Number(this.current.step) || 1), true);
      } else if (control.hasAttribute('data-le-quantity-preset')) {
        if (!this.busy) this.setQuantity(Number(control.dataset.leQuantityPreset), true);
      } else if (control.hasAttribute('data-le-gallery-step')) {
        this.goToImage(this.galleryIndex + Number(control.dataset.leGalleryStep));
      } else if (control.hasAttribute('data-le-media')) {
        this.goToImage(this.galleryItems.findIndex(item => String(item.id) === control.dataset.leMedia));
      } else if (control.hasAttribute('data-le-zoom')) {
        this.openZoom(control);
      }
    }

    renderGallery(variant) {
      if (!this.track) return;
      this.galleryItems = (variant.mediaIds || []).map(id => this.media.get(String(id))).filter(Boolean);
      if (!this.galleryItems.length) this.galleryItems = [...this.media.values()];
      const existingIds = [...this.track.querySelectorAll('[data-le-slide]')].map(slide => slide.dataset.leSlide);
      const same = existingIds.join(',') === this.galleryItems.map(item => String(item.id)).join(',');
      if (!same) {
        const slides = document.createDocumentFragment();
        const thumbs = document.createDocumentFragment();
        this.galleryItems.forEach((item, index) => {
          const figure = document.createElement('figure');
          figure.className = 'le-pdp-slide';
          if (String(item.id) === String(this.config.collectionMediaId)) figure.classList.add('le-pdp-slide--collection');
          figure.dataset.leSlide = item.id;
          const zoom = document.createElement('button');
          zoom.type = 'button';
          zoom.className = 'le-pdp-zoom-trigger';
          zoom.dataset.leZoom = '';
          zoom.setAttribute('aria-label', `${this.config.zoomLabel || this.config.galleryLabels?.zoom || 'Enlarge image'}: ${item.alt || variant.label || variant.title}`);
          const image = document.createElement('img');
          image.src = item.src;
          if (item.srcset) image.srcset = item.srcset;
          image.sizes = '(min-width: 1024px) 55vw, 100vw';
          image.width = item.width;
          image.height = item.height;
          image.alt = item.alt || variant.label || variant.title;
          image.loading = index ? 'lazy' : 'eager';
          image.decoding = 'async';
          zoom.append(image);
          figure.append(zoom);
          slides.append(figure);
          const thumbnail = document.createElement('button');
          thumbnail.type = 'button';
          thumbnail.className = 'le-pdp-thumb';
          thumbnail.dataset.leMedia = item.id;
          thumbnail.setAttribute('aria-label', `${this.config.imageLabel || 'View image'} ${index + 1}: ${item.alt || variant.label || variant.title}`);
          const thumbnailImage = document.createElement('img');
          const thumbnailUrl = new URL(item.src, window.location.href);
          thumbnailUrl.searchParams.set('width', '160');
          thumbnailImage.src = thumbnailUrl.toString();
          thumbnailImage.width = 80;
          thumbnailImage.height = 80;
          thumbnailImage.alt = '';
          thumbnailImage.loading = 'lazy';
          thumbnail.append(thumbnailImage);
          thumbs.append(thumbnail);
        });
        this.track.replaceChildren(slides);
        this.thumbs?.replaceChildren(thumbs);
      }
      this.galleryIndex = 0;
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.renderGalleryCounter();
    }

    goToImage(index) {
      if (!this.track || !this.galleryItems.length) return;
      const bounded = Math.max(0, Math.min(this.galleryItems.length - 1, index));
      const slide = this.track.querySelectorAll('[data-le-slide]')[bounded];
      const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({ left, behavior: motion() });
      this.galleryIndex = bounded;
      this.updateGalleryControls();
    }

    renderGalleryCounter() {
      if (!this.track || !this.galleryItems?.length) return;
      const left = this.track.getBoundingClientRect().left;
      let distance = Infinity;
      this.track.querySelectorAll('[data-le-slide]').forEach((slide, index) => {
        const currentDistance = Math.abs(slide.getBoundingClientRect().left - left);
        if (currentDistance < distance) { distance = currentDistance; this.galleryIndex = index; }
      });
      this.updateGalleryControls();
    }

    updateGalleryControls() {
      const isCollection = String(this.galleryItems[this.galleryIndex]?.id) === String(this.config.collectionMediaId);
      this.toggle('[data-le-collection-caption]', isCollection);
      this.root.dataset.leCollectionVisible = String(isCollection);
      this.text('[data-le-gallery-counter]', `${this.galleryIndex + 1} / ${this.galleryItems.length}`);
      this.root.querySelectorAll('[data-le-media]').forEach((button, index) => {
        const active = index === this.galleryIndex;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-current', String(active));
        button.setAttribute('aria-pressed', String(active));
      });
      this.root.querySelectorAll('[data-le-gallery-step]').forEach(button => {
        button.disabled = Number(button.dataset.leGalleryStep) < 0 ? this.galleryIndex === 0 : this.galleryIndex >= this.galleryItems.length - 1;
      });
    }

    openZoom(trigger) {
      if (!this.dialog || this.dialog.open) return;
      const selectedId = trigger.closest('[data-le-slide]')?.dataset.leSlide;
      const item = this.media.get(selectedId) || this.galleryItems[this.galleryIndex];
      const image = this.dialog.querySelector('[data-le-zoom-image]');
      image.src = item.zoom || item.src;
      image.alt = item.alt || this.current.label || this.current.title;
      image.width = item.width;
      image.height = item.height;
      this.zoomReturn = trigger;
      this.dialog.showModal();
      this.dialog.querySelector('[data-le-zoom-close]')?.focus();
      this.scheduleSticky();
    }

    closeZoom() { this.dialog?.close(); }

    modalOpen() {
      if (document.querySelector('.drawer--visible, dialog[open], .pswp--open, [data-quickview-modal].is-open')) return true;
      return [...document.querySelectorAll('[role="dialog"][aria-modal="true"], #freegifts-main-popup-container')].some(element => {
        if (element.closest('[hidden], [aria-hidden="true"]')) return false;
        const drawer = element.closest('[data-drawer]');
        if (drawer && !drawer.classList.contains('drawer--visible')) return false;
        const style = window.getComputedStyle(element);
        return element.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
      });
    }

    scheduleSticky() {
      if (!this.sticky || this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        const visible = window.matchMedia('(max-width: 767px)').matches && this.mainButton.getBoundingClientRect().bottom <= 0 && !this.modalOpen();
        this.sticky.hidden = !visible;
        this.root.dataset.leStickyVisible = String(visible);
      });
    }

    returnToPurchase(offer) {
      const preferred = offer ? `[data-le-offer="${offer}"]` : '[data-le-offer][aria-pressed="true"]';
      const target = [
        this.root.querySelector(preferred),
        this.root.querySelector('[data-le-variant][aria-pressed="true"]'),
        this.mainButton
      ].find(candidate => candidate && !candidate.closest('[hidden]'));
      const controls = this.root.querySelector('.le-pdp-purchase-controls');
      const destination = window.matchMedia('(max-width: 899px)').matches && controls ? controls : this.root;
      destination.scrollIntoView({ behavior: motion(), block: 'start' });
      target?.focus({ preventScroll: true });
    }

    destroy() {
      this.controller.abort();
      this.observer?.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
      instances.delete(this.root);
    }
  }

  const initialize = (scope = document) => {
    const roots = [...scope.querySelectorAll('[data-le-product]')];
    if (scope.matches?.('[data-le-product]')) roots.unshift(scope);
    roots.forEach(root => {
      if (!instances.has(root) && root.querySelector('[data-le-config]')) instances.set(root, new LoveEcruProduct(root));
      else instances.get(root)?.installNativeBridge();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('alpine:initialized', () => initialize());
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-le-product]').forEach(root => instances.get(root)?.destroy());
  });
})();
